/* eslint-disable jsx-a11y/anchor-has-content */
import { Avatar, Box, Button, Paper, Typography } from '@mui/material';
import React, { memo, useCallback, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis as RechartsPolarAngleAxis,
  type PolarAngleAxisProps,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useSelector } from 'react-redux';
import senaface from '../../../assets/paige.jpeg';
import { selectCurrentUser } from '../../../layouts/MainLayout/slice/selectors';
import { SENDER_TYPES } from '../slice/constants';
import useExportChartHandler from '../hooks/useExportChartHandler';

// Recharts types PolarAngleAxis as returning ReactNode, which React 19 JSX rejects
const PolarAngleAxis = RechartsPolarAngleAxis as React.FC<PolarAngleAxisProps>;
const MARKDOWN_PLUGINS = [remarkGfm];
const CHART_TICK = { fontSize: 12 };
const CHART_ANIMATION_PROPS = { isAnimationActive: false };

interface ChatTimeLineItemProps {
  assistantResponse: string | React.ReactNode;
  sender: (typeof SENDER_TYPES)[keyof typeof SENDER_TYPES];
}

type ChartType = 'bar' | 'line' | 'area' | 'pie' | 'donut' | 'scatter' | 'radar' | 'composed';

type ChartSeries = {
  key: string;
  type: 'bar' | 'line' | 'area';
  name?: string;
};

type ChartSpec = {
  type: ChartType;
  title?: string;
  xKey?: string;
  yKeys?: string[];
  unit?: string;
  data: Record<string, string | number>[];
  series?: ChartSeries[];
};

const CHART_COLORS = ['#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#d32f2f', '#0288d1', '#7b1fa2', '#388e3c'];

function MarkdownChart({ spec }: { spec: ChartSpec }) {
  const { chartContainerRef, exportChart } = useExportChartHandler();
  const xKey = useMemo(() => {
    if (spec.xKey) {
      return spec.xKey;
    }

    const firstDatum = spec.data?.[0];
    if (!firstDatum) {
      return '';
    }

    const keys = Object.keys(firstDatum);
    return keys.find((key) => typeof firstDatum[key] === 'string') || keys[0] || '';
  }, [spec.data, spec.xKey]);
  const yKeys = useMemo(() => {
    if (Array.isArray(spec.yKeys) && spec.yKeys.length) {
      return spec.yKeys;
    }

    const firstDatum = spec.data?.[0];
    if (!firstDatum) {
      return [];
    }

    const preferredKey = ['value', 'count', 'total', 'amount', 'percentage'].find((key) => key !== xKey && typeof firstDatum[key] === 'number');
    if (preferredKey) {
      return [preferredKey];
    }

    return Object.keys(firstDatum).filter((key) => key !== xKey && typeof firstDatum[key] === 'number');
  }, [spec.data, spec.yKeys, xKey]);
  const firstYKey = yKeys[0];
  const tooltipFormatter = useCallback((value: number | string | readonly (number | string)[] | undefined) => {
    if (value == null) {
      return ['—'];
    }

    const displayValue = Array.isArray(value) ? value.join(', ') : value;
    return spec.unit ? [`${displayValue} ${spec.unit}`] : [displayValue];
  }, [spec.unit]);

  if (!spec?.data?.length || !spec.type || !xKey || !firstYKey) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', minWidth: 280, my: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        {spec.title ? (
          <Typography variant="subtitle2">{spec.title}</Typography>
        ) : (
          <span />
        )}
        <Button size="small" onClick={() => exportChart(true)}>
          Export as SVG
        </Button>
      </Box>

      <Box ref={chartContainerRef} sx={{ width: '100%', height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
        {spec.type === 'line' ? (
          <LineChart data={spec.data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={CHART_TICK} />
            <YAxis tick={CHART_TICK} />
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
            {yKeys.map((key, index) => (
              <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[index % CHART_COLORS.length]} strokeWidth={2} {...CHART_ANIMATION_PROPS} />
            ))}
          </LineChart>
        ) : spec.type === 'area' ? (
          <AreaChart data={spec.data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={CHART_TICK} />
            <YAxis tick={CHART_TICK} />
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
            {yKeys.map((key, index) => (
              <Area key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[index % CHART_COLORS.length]} fill={CHART_COLORS[index % CHART_COLORS.length]} fillOpacity={0.25} {...CHART_ANIMATION_PROPS} />
            ))}
          </AreaChart>
        ) : spec.type === 'pie' || spec.type === 'donut' ? (
          <PieChart>
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
            <Pie data={spec.data} dataKey={firstYKey} nameKey={xKey} innerRadius={spec.type === 'donut' ? 55 : 0} outerRadius={95} label {...CHART_ANIMATION_PROPS}>
              {spec.data.map((_, index) => (
                <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
        ) : spec.type === 'scatter' ? (
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} name={xKey} type="number" tick={CHART_TICK} />
            <YAxis dataKey={firstYKey} name={firstYKey} type="number" tick={CHART_TICK} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={tooltipFormatter} />
            <Legend />
            <Scatter name={spec.title || firstYKey} data={spec.data} fill={CHART_COLORS[0]} {...CHART_ANIMATION_PROPS} />
          </ScatterChart>
        ) : spec.type === 'radar' ? (
          <RadarChart data={spec.data}>
            <PolarGrid />
            <PolarAngleAxis dataKey={xKey} tick={CHART_TICK} />
            <PolarRadiusAxis tick={CHART_TICK} />
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
            {yKeys.map((key, index) => (
              <Radar key={key} dataKey={key} stroke={CHART_COLORS[index % CHART_COLORS.length]} fill={CHART_COLORS[index % CHART_COLORS.length]} fillOpacity={0.25} {...CHART_ANIMATION_PROPS} />
            ))}
          </RadarChart>
        ) : spec.type === 'composed' ? (
          <ComposedChart data={spec.data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={CHART_TICK} />
            <YAxis tick={CHART_TICK} />
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
            {(spec.series?.length ? spec.series : yKeys.map((key): ChartSeries => ({ key, type: 'bar' }))).map((series, index) => {
              const color = CHART_COLORS[index % CHART_COLORS.length];

              if (series.type === 'line') {
                return <Line key={series.key} type="monotone" dataKey={series.key} name={series.name || series.key} stroke={color} strokeWidth={2} {...CHART_ANIMATION_PROPS} />;
              }

              if (series.type === 'area') {
                return <Area key={series.key} type="monotone" dataKey={series.key} name={series.name || series.key} stroke={color} fill={color} fillOpacity={0.25} {...CHART_ANIMATION_PROPS} />;
              }

              return <Bar key={series.key} dataKey={series.key} name={series.name || series.key} fill={color} {...CHART_ANIMATION_PROPS} />;
            })}
          </ComposedChart>
        ) : (
          <BarChart data={spec.data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={CHART_TICK} />
            <YAxis tick={CHART_TICK} />
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
            {yKeys.map((key, index) => (
              <Bar key={key} dataKey={key} fill={CHART_COLORS[index % CHART_COLORS.length]} {...CHART_ANIMATION_PROPS} />
            ))}
          </BarChart>
        )}
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

function isChartCodeBlock(children: React.ReactNode) {
  const child = React.Children.toArray(children)[0];

  return React.isValidElement<{ className?: string }>(child) && child.props.className === 'language-chart';
}

const MARKDOWN_COMPONENTS: Components = {
  p: (props) => <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 1, wordBreak: 'break-word' }} {...props} />,
  ol: (props) => (
    <ol
      style={{
        paddingLeft: '1.25rem',
        marginBottom: '0.5rem',
        fontSize: '0.875rem',
      }}
      {...props}
    />
  ),
  ul: (props) => (
    <ul
      style={{
        paddingLeft: '1.25rem',
        marginBottom: '0.5rem',
        fontSize: '0.875rem',
      }}
      {...props}
    />
  ),
  li: (props) => (
    <li style={{ marginLeft: 16 }}>
      <Typography component="span" variant="body2" sx={{ fontSize: '0.875rem', wordBreak: 'break-word' }} {...props} />
    </li>
  ),
  h1: (props) => <Typography variant="h6" sx={{ mb: 1 }} {...props} />,
  h2: (props) => <Typography variant="subtitle1" sx={{ mb: 1 }} {...props} />,
  a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />,
  pre: ({ children, ...props }) => {
    if (isChartCodeBlock(children)) {
      return <>{children}</>;
    }

    return (
      <Box component="pre" sx={{ overflowX: 'auto', p: 1.5, borderRadius: 1, bgcolor: 'grey.100', fontSize: '0.8rem' }} {...props}>
        {children}
      </Box>
    );
  },
  code: ({ className, children, ...props }) => {
    const language = className?.replace('language-', '');
    const value = String(children).trim();

    if (language === 'chart') {
      try {
        return <MarkdownChart spec={JSON.parse(value)} />;
      } catch {
        return null;
      }
    }

    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
};

function UserAvatar() {
  const currentUser = useSelector(selectCurrentUser);

  return <Avatar sx={{ width: 32, height: 32, border: 0 }}>{currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}</Avatar>;
}

function ChatTimeLineItem({ assistantResponse, sender }: ChatTimeLineItemProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: sender === SENDER_TYPES.USER ? 'flex-end' : 'flex-start',
        alignItems: 'flex-start',
        gap: 1,
      }}
    >
      {sender === SENDER_TYPES.ASSISTANT && <Avatar src={senaface} alt="Sena" sx={{ border: 0, width: 32, height: 32 }} />}

      <Paper
        sx={{
          py: 1,
          px: 3,
          maxWidth: '90%',
          backgroundColor: sender === SENDER_TYPES.ASSISTANT ? 'transparent' : 'grey.100',
          color: 'text.primary',
          boxShadow: 'none',
        }}
      >
        {typeof assistantResponse === 'string' ? (
          <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS} components={MARKDOWN_COMPONENTS}>
            {assistantResponse}
          </ReactMarkdown>
        ) : (
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {assistantResponse}
          </Typography>
        )}
      </Paper>

      {sender === SENDER_TYPES.USER && <UserAvatar />}
    </Box>
  );
}

export default memo(ChatTimeLineItem);